/**
 * 2026-08-15 (Anke, "clean rewrite") — the single, pure implementation of the
 * paragraph/heading/listItem "own strut" fix, shared by BOTH the edit overlay
 * (`textbox-editor.controller.ts`, via a thin `ProseMirrorNode.toJSON()`
 * adapter) and the view-mode raster (`textbox-html-builder.ts`, via a thin
 * `domRunsToStrutJson` DOM-walk adapter).
 *
 * Why this exists: a `<p>`/`<h*>` is itself an inline formatting context and
 * generates its own line-box "strut" from ITS OWN font-size — a plain CSS
 * fact (CSS3 §10.8), independent of whatever smaller size its child runs
 * carry. Before this file existed, the fix for that fact was written TWICE,
 * independently, in two different files with two different data shapes
 * (`ProseMirrorNode` vs raw DOM) — and the second copy never got written at
 * all for the view-mode raster, which is exactly why edit mode and view mode
 * kept disagreeing. One algorithm, two ~5-line adapters: the algorithm can no
 * longer drift out of sync with itself.
 */

/** A minimal, JSON-shaped TipTap/ProseMirror document node — just enough to
 * walk marks and children. Matches `ProseMirrorNode.toJSON()`'s shape. */
export interface StrutJsonNode {
  readonly type?: string;
  readonly marks?: ReadonlyArray<{ readonly type: string; readonly attrs?: Record<string, unknown> }>;
  readonly content?: ReadonlyArray<StrutJsonNode>;
}

/**
 * Parse a bare absolute px string (e.g. `'16px'`) to its numeric level.
 * Returns `null` for relative units (`em`/`%`/`rem`) or malformed values so
 * only cluster-world-unit px marks are considered.
 */
export function parseFontSizePx(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = /^([\d.]+)px$/.exec(value.trim());
  return match ? parseFloat(match[1]!) : null;
}

function isTextNode(node: StrutJsonNode): boolean {
  return node.type === 'text' && !node.content;
}

/**
 * A leaf whose size can set the line height: a text run OR a hard break. A
 * break carries its own `textStyle` (e.g. pasted from PowerPoint, Anke
 * 2026-10-02), and its span sizes the line like a run does. Only the strut
 * (`maxRun…`) counts breaks; the list marker (`firstRun…`) still follows the
 * first TEXT run, so an unmarked leading break does not unset it.
 */
function isSizedLeaf(node: StrutJsonNode): boolean {
  return (node.type === 'text' || node.type === 'hardBreak') && !node.content;
}

function fontSizeOf(node: StrutJsonNode): number | null {
  const mark = node.marks?.find((m) => m.type === 'textStyle');
  return parseFontSizePx(mark?.attrs?.['fontSize']);
}

/**
 * The largest stored px level among ALL descendant text runs and hard breaks
 * under `node` (any depth) that carry a `textStyle.fontSize` mark, or `null`
 * when none of them are explicitly sized. Sizing a paragraph/heading from this kills the
 * CSS §10.8 strut mismatch in both directions: never smaller than the
 * tallest run actually needs (so it never wins over a genuinely larger run),
 * never bigger (so it never pins a genuinely smaller line tall). A block with
 * no marked runs at all is left alone (`null`) — it's uniformly at the
 * textbox's base size already, nothing to correct.
 */
export function maxRunFontSizePxFromJson(node: StrutJsonNode): number | null {
  if (isSizedLeaf(node)) return fontSizeOf(node);
  let max: number | null = null;
  for (const child of node.content ?? []) {
    const level = maxRunFontSizePxFromJson(child);
    if (level !== null && (max === null || level > max)) max = level;
  }
  return max;
}

/**
 * The stored px level of the FIRST descendant text run under `node` (any
 * depth, document order), or `null` when that first run carries no
 * `textStyle.fontSize` mark. Latches on the first text node found and
 * ignores the rest — a LATER sized run must never override an unsized first
 * run. Used to size a list item's `::marker` from the text it labels (the
 * marker sits next to the first line, not the tallest one).
 */
export function firstRunFontSizePxFromJson(node: StrutJsonNode): number | null {
  const result = firstTextNode(node);
  return result ? fontSizeOf(result) : null;
}

function firstTextNode(node: StrutJsonNode): StrutJsonNode | null {
  if (isTextNode(node)) return node;
  for (const child of node.content ?? []) {
    const hit = firstTextNode(child);
    if (hit) return hit;
  }
  return null;
}
